import _ from 'lodash';
import { useCallback } from 'react';
import { ApolloCache, DocumentNode, useApolloClient } from '@apollo/client';

// Data
import { GET_CURR_MEMBER } from '../queries/member.queries';
import {
  AGE_GROUP_SELECTION_SCOPE,
  ASSESSMENT_SESSION_SELECTION_SCOPE,
  CALIBRATION_ASSIGNMENT_SELECTION_SCOPE,
  DRILL_SELECTION_SCOPE,
  PLAYER_ASSESSMENT_SELECTION_SCOPE,
} from '../queries/selection-target.queries';
import { APPLY_SELECTION_TARGET } from '../queries/user.queries';
import { isCachedScopeCurrent, toSelectionTargetInput } from '../helpers/selection-target.helpers';
import {
  IActiveSelection, ICachedSelectionScope, ISelectionTarget, ISelectionTargetResult,
} from '../types/selection-target.types';
import { setAuthUserSettings, staffUser } from './auth.service';

type ScopeReader = (cache: ApolloCache<unknown>, id: string) => ICachedSelectionScope | null;

// A record the cache lacks, or holds without these fields, reads as unknown.
const readCached = <T, >(cache: ApolloCache<unknown>, __typename: string, _id: string, fragment: DocumentNode): T | null => {
  try {
    return cache.readFragment<T>({ id: cache.identify({ __typename, _id }), fragment });
  } catch {
    return null;
  }
};

const readThroughAgeGroup = (typename: string, fragment: DocumentNode): ScopeReader => (cache, id) => {
  const ageGroup = readCached<{ ageGroup: { _id: string; assessmentId: string } | null }>(cache, typename, id, fragment)?.ageGroup;
  return ageGroup?.assessmentId ? { assessmentId: ageGroup.assessmentId, ageGroupId: ageGroup._id } : null;
};

const readCompany = (typename: string, fragment: DocumentNode): ScopeReader => (cache, id) => {
  const record = readCached<{ companyId: string | null }>(cache, typename, id, fragment);
  return record ? { companyId: record.companyId } : null;
};

const SCOPE_READERS: Partial<Record<keyof ISelectionTarget, ScopeReader>> = {
  playerAssessmentId: readThroughAgeGroup('PlayerAssessment', PLAYER_ASSESSMENT_SELECTION_SCOPE),
  assessmentSessionId: readThroughAgeGroup('AssessmentSession', ASSESSMENT_SESSION_SELECTION_SCOPE),
  ageGroupId: (cache, id) => {
    const assessmentId = readCached<{ assessmentId: string }>(cache, 'AgeGroup', id, AGE_GROUP_SELECTION_SCOPE)?.assessmentId;
    return assessmentId ? { assessmentId, ageGroupId: id } : null;
  },
  drillId: readCompany('Drill', DRILL_SELECTION_SCOPE),
  calibrationAssignmentId: readCompany('CalibrationAssignment', CALIBRATION_ASSIGNMENT_SELECTION_SCOPE),
};

const readCachedSelectionScope = (cache: ApolloCache<unknown>, target: ISelectionTarget): ICachedSelectionScope | null => {
  const key = (Object.keys(SCOPE_READERS) as Array<keyof ISelectionTarget>).find((field) => !!target[field]);
  const reader = key && SCOPE_READERS[key];
  return reader ? reader(cache, String(target[key as keyof ISelectionTarget])) : null;
};

// HOOK: whether the cache alone shows a path target already inside the selection — no network.
export const useIsRouteTargetCurrent = () => {
  const client = useApolloClient();

  return useCallback(
    (target: ISelectionTarget, selection?: IActiveSelection) => isCachedScopeCurrent(readCachedSelectionScope(client.cache, target), selection),
    [client],
  );
};

// HOOK: applies a targeted link through the backend, which checks access and stores the selection in one
// write; the stored selection is mirrored locally and, when the association changed, the member reloaded.
export const useApplySelectionTarget = () => {
  const client = useApolloClient();

  return useCallback(async (target: ISelectionTarget): Promise<ISelectionTargetResult> => {
    // No optimistic result: whether the link is granted, and what it resolves to, is decided by the server.
    const { data } = await client.mutate<{ applySelectionTarget: ISelectionTargetResult }>({
      mutation: APPLY_SELECTION_TARGET,
      variables: { target: toSelectionTargetInput(target) },
    });
    const result = data?.applySelectionTarget;
    if (!result) throw new Error('applySelectionTarget returned no result');
    if (!result.granted || !result.user) return result;

    const previousCompanyId = staffUser()?.settings?.activeCompanyId;
    // The Apollo result carries a __typename that is not a setting, so it stays out of the stored user.
    setAuthUserSettings(_.omit(result.user.settings, '__typename'));
    if (result.user.settings.activeCompanyId === previousCompanyId) return result;

    try {
      await client.query({ query: GET_CURR_MEMBER, fetchPolicy: 'network-only' });
    } catch (error) {
      console.error('applySelectionTarget member reload failed:', error);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'getCurrMember' });
      client.cache.gc();
    }
    return result;
  }, [client]);
};
